import { useEffect, useState } from 'react';
import {
  Users,
  Loader2,
  Plus,
  MessageSquare,
  CheckCircle2,
  XCircle,
  AlertCircle,
  X,
  Send,
  Clock,
  ChevronLeft,
} from 'lucide-react';
import { resolveContractContext } from '../utils/contractResolver';
import { getSelectedText, isWordApiAvailable } from '../utils/wordApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const API_TOKEN = import.meta.env.VITE_ADDIN_API_TOKEN || '';

interface UserLite {
  id: string;
  name: string | null;
  email: string;
  first_name: string | null;
  last_name: string | null;
}

type ReviewStatus = 'Open' | 'InReview' | 'Resolved' | 'Cancelled';
type ResponseStatus = 'Pending' | 'Approved' | 'Rejected' | 'ChangesRequested';

interface Assignee {
  id: string;
  user_id: string;
  response: ResponseStatus;
  response_note: string | null;
  responded_at: string | null;
  user: UserLite;
}

interface ReviewListItem {
  id: string;
  title: string;
  anchor_text: string;
  status: ReviewStatus;
  created_at: string;
  requested_by: UserLite;
  assignees: Assignee[];
  _count: { comments: number };
}

interface Comment {
  id: string;
  body: string;
  suggested_text: string | null;
  created_at: string;
  author: UserLite;
}

interface ReviewDetail extends ReviewListItem {
  comments: Comment[];
}

interface ContractCtx {
  contractId: string;
  tenantId: string;
}

function displayName(u: UserLite | undefined | null) {
  if (!u) return 'Unknown';
  return u.name || u.email || `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'User';
}

function statusColor(s: ReviewStatus): string {
  switch (s) {
    case 'Open':
      return 'bg-blue-100 text-blue-700';
    case 'InReview':
      return 'bg-amber-100 text-amber-700';
    case 'Resolved':
      return 'bg-green-100 text-green-700';
    case 'Cancelled':
      return 'bg-gray-100 text-gray-600';
  }
}

function responseColor(r: ResponseStatus): string {
  switch (r) {
    case 'Pending':
      return 'bg-gray-100 text-gray-600';
    case 'Approved':
      return 'bg-green-100 text-green-700';
    case 'Rejected':
      return 'bg-red-100 text-red-700';
    case 'ChangesRequested':
      return 'bg-amber-100 text-amber-700';
  }
}

function getCurrentUserId(): string | null {
  try {
    const raw = localStorage.getItem('userdata');
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data?.id || data?.user_id || null;
  } catch {
    return null;
  }
}

export function ClauseCollaboration() {
  const [ctx, setCtx] = useState<ContractCtx | null>(null);
  const [reviews, setReviews] = useState<ReviewListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const userId = getCurrentUserId();

  useEffect(() => {
    let cancelled = false;
    const tryResolve = async () => {
      // First try the cached / shared resolver.
      let c = await resolveContractContext().catch(() => null);
      // If still missing contractId, retry once after a short delay — Office's
      // document.settings sometimes arrives late on a fresh component mount.
      if (!c?.contractId && c?.tenantId) {
        await new Promise((r) => setTimeout(r, 600));
        c = await resolveContractContext({ force: true }).catch(() => null);
      }
      if (cancelled) return;
      if (c?.contractId && c?.tenantId) {
        setCtx({ contractId: c.contractId, tenantId: c.tenantId });
      } else if (c?.tenantId) {
        // Partial context — we have a tenant but no contract. The reviews
        // backend can still list reviews scoped to the tenant, but Create
        // needs a contract. Show a friendlier message and keep the screen
        // usable (no scary error).
        setCtx(null);
        setError(
          'No contract is open in Word. Open this add-in from a CLM contract to start new reviews.',
        );
        setLoading(false);
      } else {
        setError('Could not resolve contract context. Try reloading the add-in.');
        setLoading(false);
      }
    };
    tryResolve();
    return () => {
      cancelled = true;
    };
  }, []);

  const reload = async () => {
    if (!ctx) return;
    try {
      setLoading(true);
      const url = `${API_BASE_URL}/api/addin/reviews?tenantId=${encodeURIComponent(
        ctx.tenantId,
      )}&contractId=${encodeURIComponent(ctx.contractId)}`;
      const res = await fetch(url, {
        headers: { 'x-addin-token': API_TOKEN, 'ngrok-skip-browser-warning': 'true' },
      });
      if (!res.ok) throw new Error('Failed to load reviews');
      const data = await res.json();
      setReviews(data.reviews || []);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ctx) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.contractId]);

  if (openId && ctx) {
    return (
      <ReviewDetail
        ctx={ctx}
        reviewId={openId}
        currentUserId={userId}
        onBack={() => {
          setOpenId(null);
          reload();
        }}
      />
    );
  }

  return (
    <div
      className="flex-1 min-h-0 flex flex-col overflow-hidden"
      style={{ background: 'var(--bg-soft)', fontSize: '13px' }}
    >
      <div className="px-3 py-2.5 border-b" style={{ borderColor: 'var(--line)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded grid place-items-center"
              style={{ background: 'var(--brand-tint)' }}
            >
              <Users size={14} style={{ color: 'var(--brand-3)' }} />
            </div>
            <div>
              <h1 className="text-[13px] font-semibold text-[var(--ink)]">
                Clause reviews
              </h1>
              <p className="text-[10.5px] text-[var(--ink-3)]">
                Send passages to your team for review
              </p>
            </div>
          </div>
          {ctx && (
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-white rounded-md text-[11px]"
              style={{ background: 'var(--brand)' }}
            >
              <Plus size={12} /> New
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {error && (
          <div className="p-2.5 bg-red-50 border border-red-200 rounded text-red-700 text-[11.5px]">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8 text-[var(--ink-3)]">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : reviews.length === 0 && !error ? (
          <div className="bg-white border rounded-lg p-6 text-center text-[var(--ink-3)] text-[12px]" style={{ borderColor: 'var(--line)' }}>
            <MessageSquare className="mx-auto mb-2 text-gray-400" size={22} />
            No reviews yet. Highlight text in the document and click "New".
          </div>
        ) : (
          reviews.map((r) => (
            <button
              key={r.id}
              onClick={() => setOpenId(r.id)}
              className="w-full text-left bg-white border rounded-lg p-2.5 hover:border-[var(--brand-3)] transition"
              style={{ borderColor: 'var(--line)' }}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="text-[12px] font-semibold text-[var(--ink)] truncate flex-1">
                  {r.title}
                </h3>
                <span
                  className={`px-1.5 py-0.5 text-[10px] rounded-full ${statusColor(r.status)}`}
                >
                  {r.status === 'InReview' ? 'In review' : r.status}
                </span>
              </div>
              <p className="text-[11px] text-[var(--ink-3)] line-clamp-2 mb-1.5">
                "{r.anchor_text}"
              </p>
              <div className="flex items-center justify-between text-[10px] text-[var(--ink-4)]">
                <span>{displayName(r.requested_by)}</span>
                <span className="flex items-center gap-1">
                  <MessageSquare size={9} /> {r._count.comments} ·{' '}
                  {r.assignees.length} reviewer{r.assignees.length === 1 ? '' : 's'}
                </span>
              </div>
            </button>
          ))
        )}
      </div>

      {createOpen && ctx && userId && (
        <CreateReviewSheet
          ctx={ctx}
          userId={userId}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            reload();
          }}
        />
      )}
      {createOpen && !userId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center p-3" onClick={() => setCreateOpen(false)}>
          <div className="bg-white rounded-lg p-4 max-w-sm text-[12px]">
            User identity not loaded — reload the add-in.
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------- Create review sheet ---------------------- */

function CreateReviewSheet({
  ctx,
  userId,
  onClose,
  onCreated,
}: {
  ctx: ContractCtx;
  userId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState('');
  const [anchorText, setAnchorText] = useState('');
  const [users, setUsers] = useState<UserLite[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Pre-fill anchor with current Word selection
  useEffect(() => {
    if (isWordApiAvailable()) {
      getSelectedText()
        .then((text) => {
          if (text && text.trim()) setAnchorText(text.trim());
        })
        .catch(() => {});
    }
  }, []);

  // Load users in tenant
  useEffect(() => {
    fetch(
      `${API_BASE_URL}/api/addin/users-public?tenantId=${encodeURIComponent(ctx.tenantId)}&limit=200`,
      {
        headers: { 'x-addin-token': API_TOKEN, 'ngrok-skip-browser-warning': 'true' },
      },
    )
      .then((r) => r.json())
      .then((data) => {
        const arr = Array.isArray(data?.users) ? data.users : [];
        setUsers(arr.filter((u: UserLite) => u.id !== userId));
      })
      .catch(() => {});
  }, [ctx.tenantId, userId]);

  const submit = async () => {
    if (!title.trim() || !anchorText.trim() || selected.size === 0) {
      setErr('Title, passage and at least one reviewer are required');
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/addin/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-addin-token': API_TOKEN,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          tenantId: ctx.tenantId,
          contractId: ctx.contractId,
          userId,
          title: title.trim(),
          anchorText: anchorText.trim(),
          assigneeIds: Array.from(selected),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `Create failed (${res.status})`);
      }
      onCreated();
    } catch (e: any) {
      setErr(e?.message || 'Failed to create');
      setSubmitting(false);
    }
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex flex-col" style={{ fontSize: '13px' }}>
      <div className="flex items-center justify-between px-3 py-2.5 bg-white border-b" style={{ borderColor: 'var(--line)' }}>
        <h3 className="font-semibold text-[var(--ink)]">New clause review</h3>
        <button onClick={onClose} className="p-1 text-[var(--ink-3)]">
          <X size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-white">
        <div>
          <label className="block text-[11px] font-medium text-[var(--ink-2)] mb-1">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Payment terms"
            className="w-full px-2.5 py-1.5 border rounded-md text-[12.5px] focus:outline-none focus:ring-2 focus:ring-[var(--brand)]"
            style={{ borderColor: 'var(--line)' }}
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-[var(--ink-2)] mb-1">
            Clause text (pulled from your Word selection)
          </label>
          <textarea
            value={anchorText}
            onChange={(e) => setAnchorText(e.target.value)}
            rows={5}
            placeholder="Select text in Word, or paste here…"
            className="w-full px-2.5 py-1.5 border rounded-md text-[12.5px] focus:outline-none focus:ring-2 focus:ring-[var(--brand)]"
            style={{ borderColor: 'var(--line)' }}
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-[var(--ink-2)] mb-1">
            Reviewers ({selected.size} selected)
          </label>
          <div className="border rounded-md max-h-44 overflow-y-auto" style={{ borderColor: 'var(--line)' }}>
            {users.length === 0 ? (
              <div className="p-2.5 text-[11.5px] text-[var(--ink-3)]">No teammates available.</div>
            ) : (
              users.map((u) => (
                <label
                  key={u.id}
                  className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  style={{ borderColor: 'var(--line)' }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(u.id)}
                    onChange={() => toggle(u.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] text-[var(--ink)] truncate">{displayName(u)}</div>
                    <div className="text-[10.5px] text-[var(--ink-3)] truncate">{u.email}</div>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>
        {err && (
          <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-[11px]">
            {err}
          </div>
        )}
      </div>
      <div className="border-t px-3 py-2 bg-white flex items-center justify-end gap-2" style={{ borderColor: 'var(--line)' }}>
        <button onClick={onClose} className="px-3 py-1.5 text-[12px] text-[var(--ink-2)]">
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={submitting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-white text-[12px] rounded-md disabled:opacity-50"
          style={{ background: 'var(--brand)' }}
        >
          {submitting ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          Create
        </button>
      </div>
    </div>
  );
}

/* ---------------------- Review detail ---------------------- */

function ReviewDetail({
  ctx,
  reviewId,
  currentUserId,
  onBack,
}: {
  ctx: ContractCtx;
  reviewId: string;
  currentUserId: string | null;
  onBack: () => void;
}) {
  const [review, setReview] = useState<ReviewDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [posting, setPosting] = useState(false);

  const reload = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${API_BASE_URL}/api/addin/reviews/${reviewId}?tenantId=${encodeURIComponent(ctx.tenantId)}`,
        {
          headers: { 'x-addin-token': API_TOKEN, 'ngrok-skip-browser-warning': 'true' },
        },
      );
      if (res.ok) {
        const data = await res.json();
        setReview(data.review);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId]);

  const post = async () => {
    if (!commentText.trim() || !currentUserId) return;
    setPosting(true);
    try {
      await fetch(`${API_BASE_URL}/api/addin/reviews/${reviewId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-addin-token': API_TOKEN,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ tenantId: ctx.tenantId, userId: currentUserId, body: commentText.trim() }),
      });
      setCommentText('');
      await reload();
    } finally {
      setPosting(false);
    }
  };

  const respond = async (response: ResponseStatus) => {
    if (!currentUserId) return;
    await fetch(`${API_BASE_URL}/api/addin/reviews/${reviewId}/respond`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-addin-token': API_TOKEN,
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify({ tenantId: ctx.tenantId, userId: currentUserId, response }),
    });
    await reload();
  };

  const isAssignee = !!review?.assignees.find((a) => a.user_id === currentUserId);
  const closed = review?.status === 'Resolved' || review?.status === 'Cancelled';

  return (
    <div className="flex-1 min-h-0 flex flex-col" style={{ background: 'var(--bg-soft)', fontSize: '13px' }}>
      <div className="px-3 py-2 bg-white border-b flex items-center gap-2" style={{ borderColor: 'var(--line)' }}>
        <button onClick={onBack} className="p-1 text-[var(--ink-2)]">
          <ChevronLeft size={16} />
        </button>
        <h3 className="text-[13px] font-semibold text-[var(--ink)] truncate flex-1">
          {review?.title || 'Review'}
        </h3>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {loading || !review ? (
          <div className="flex items-center justify-center py-8 text-[var(--ink-3)]">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-[11px] text-[var(--ink-3)]">
              <span className={`px-1.5 py-0.5 rounded-full ${statusColor(review.status)}`}>
                {review.status === 'InReview' ? 'In review' : review.status}
              </span>
              <span>· {displayName(review.requested_by)}</span>
              <span>· {new Date(review.created_at).toLocaleDateString()}</span>
            </div>

            <div className="bg-white border-l-4 p-2.5 rounded text-[12px] text-[var(--ink)] whitespace-pre-wrap" style={{ borderLeftColor: 'var(--brand)' }}>
              {review.anchor_text}
            </div>

            <div>
              <h4 className="text-[10.5px] uppercase tracking-wide font-semibold text-[var(--ink-3)] mb-1.5">
                Reviewers
              </h4>
              <div className="space-y-1.5">
                {review.assignees.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between gap-2 bg-white border rounded px-2.5 py-1.5"
                    style={{ borderColor: 'var(--line)' }}
                  >
                    <div className="min-w-0">
                      <div className="text-[12px] text-[var(--ink)] truncate">
                        {displayName(a.user)}
                      </div>
                      <div className="text-[10px] text-[var(--ink-3)] truncate">
                        {a.user.email}
                      </div>
                    </div>
                    <span className={`shrink-0 px-1.5 py-0.5 text-[10px] rounded-full ${responseColor(a.response)}`}>
                      {a.response === 'ChangesRequested' ? 'Changes' : a.response}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {isAssignee && !closed && (
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => respond('Approved')}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-green-600 text-white text-[11px] rounded"
                >
                  <CheckCircle2 size={11} /> Approve
                </button>
                <button
                  onClick={() => respond('ChangesRequested')}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500 text-white text-[11px] rounded"
                >
                  <AlertCircle size={11} /> Changes
                </button>
                <button
                  onClick={() => respond('Rejected')}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-red-600 text-white text-[11px] rounded"
                >
                  <XCircle size={11} /> Reject
                </button>
              </div>
            )}

            <div>
              <h4 className="text-[10.5px] uppercase tracking-wide font-semibold text-[var(--ink-3)] mb-1.5 flex items-center gap-1">
                <MessageSquare size={10} /> Comments ({review.comments.length})
              </h4>
              {review.comments.length === 0 && (
                <div className="text-[11.5px] text-[var(--ink-4)] italic">No comments yet.</div>
              )}
              <div className="space-y-2">
                {review.comments.map((c) => (
                  <div key={c.id} className="bg-white border rounded p-2" style={{ borderColor: 'var(--line)' }}>
                    <div className="flex items-center gap-1 text-[10px] text-[var(--ink-3)] mb-0.5">
                      <Clock size={9} />
                      <span className="font-medium text-[var(--ink-2)]">
                        {displayName(c.author)}
                      </span>
                      <span>· {new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-[12px] text-[var(--ink)] whitespace-pre-wrap">
                      {c.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      {review && !closed && currentUserId && (
        <div className="border-t px-2.5 py-2 bg-white flex items-end gap-2" style={{ borderColor: 'var(--line)' }}>
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Comment…"
            rows={1}
            className="flex-1 px-2.5 py-1.5 border rounded-md text-[12px] resize-none focus:outline-none focus:ring-2 focus:ring-[var(--brand)]"
            style={{ borderColor: 'var(--line)', minHeight: 36 }}
          />
          <button
            onClick={post}
            disabled={posting || !commentText.trim()}
            className="h-9 inline-flex items-center gap-1 px-3 text-white text-[11px] rounded-md disabled:opacity-50"
            style={{ background: 'var(--brand)' }}
          >
            {posting ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            Post
          </button>
        </div>
      )}
    </div>
  );
}
